import { z } from "zod";

import { isHtmlEmpty } from "@/utils/rich-text";

import {
  DOT_PHRASE_SEPARATOR_MARKER_ERROR,
  DOT_PHRASE_TRIGGER_PATTERN,
} from "../constants/dot-phrases";
import { containsDotPhraseSeparatorMarker, deriveDotPhrasePlainText } from "../utils/dot-phrases";

export const DotPhraseFormSchema = z.object({
  /**
   * Chained `.min().regex()` would report both problems for a bare `.` and the resolver shows only
   * the last, telling someone who typed nothing about the character rules. One issue at a time.
   */
  trigger: z.string().superRefine((trigger, ctx) => {
    // The field is pre-filled with `.`, so "required" means "something after the dot".
    if (trigger.length < 2) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Trigger is required" });

      return;
    }

    if (!DOT_PHRASE_TRIGGER_PATTERN.test(trigger)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A trigger can only use letters, numbers, hyphens and underscores after the dot",
      });
    }
  }),
  title: z
    .string()
    .trim()
    .min(1, "Title is required")
    .refine((title) => !containsDotPhraseSeparatorMarker(title), DOT_PHRASE_SEPARATOR_MARKER_ERROR),
  // The editor holds HTML, and an empty TipTap document is `<p></p>` rather than "".
  phraseText: z
    .string()
    .refine((phraseText) => !isHtmlEmpty(phraseText), "Phrase text is required")
    // Checked against the projection, so a marker broken up by tags (`--<strong>=</strong>--`)
    // cannot slip past.
    .refine(
      (phraseText) => !containsDotPhraseSeparatorMarker(deriveDotPhrasePlainText(phraseText)),
      DOT_PHRASE_SEPARATOR_MARKER_ERROR,
    ),
});

export type DotPhraseFormType = z.infer<typeof DotPhraseFormSchema>;
